import { TemplatePortal } from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewContainerRef,
    input,
    model,
    contentChild,
    inject,
    output
} from '@angular/core';

import { ApTabLabelDirective } from '../tab-label/ap-tab-label.directive';

let nextUniqueId = 0;

/**
 * @ngdoc component
 * @name ApTabComponent
 * @module ApTabModule
 * @description
 * Component for tabs
 */
@Component({
    selector: 'ap-tab',
    templateUrl: './tab.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        /* eslint-disable @typescript-eslint/naming-convention */
        role: 'tabpanel',
        '[attr.aria-label]': 'title()',
        '[attr.aria-hidden]': '!active',
        '[attr.id]': 'id()',
        '[hidden]': '!active'
        /* eslint-enable @typescript-eslint/naming-convention */
    }
})
export class ApTabComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly viewContainerRef = inject(ViewContainerRef);

    /**
     * Required - title of tab
     */
    public readonly title = input.required<string>();

    /**
     * Id of tab. If not passed, one will be generated.
     */
    public readonly id = model<string>();

    /**
     * Is tab active?
     * Defaults to false
     */
    @Input()
    get active(): boolean {
        return this._active;
    }
    set active(active: boolean) {
        const changed: boolean = this._active !== active;
        if (changed) {
            this._active = active;
            this.changeDetectorRef.markForCheck();
            if (this.active === true) {
                // emit after change detection
                Promise.resolve().then(() => this.tabActive.emit(this._active));
            }
        }
    }

    private readonly label = contentChild(ApTabLabelDirective, { read: TemplateRef });
    readonly tabActive = output<boolean>();

    public customTabLabel: TemplatePortal | null = null;

    private uniqueId: string;
    private _active = false;

    public constructor() {
        ++nextUniqueId;
        this.uniqueId = `${nextUniqueId}`;
    }

    public ngOnInit(): void {
        if (!this.id()) {
            this.id.set(this.uniqueId + this.title());
        }

        const label = this.label();
        if (label) {
            this.customTabLabel = new TemplatePortal(label, this.viewContainerRef);
        }
    }
}
